/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles, Theme} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {FloatingIconButtonGroup} from '@ion/dls/src/components/buttons/FloatingIconButton';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import {MapZoomInIcon} from '@ion/dls/src/icons/MapZoomInIcon';
import {MapZoomOutIcon} from '@ion/dls/src/icons/MapZoomOutIcon';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {IconSize} from '@ion/dls/src/types/types';

// Defines the props for the ZoomInOutTool component.
export type ZoomInOutProps = {
  onZoomIn: React.MouseEventHandler;
  onZoomOut: React.MouseEventHandler;
  isVertical?: boolean;
  isAdminGeofence?: boolean;
};

/**
 * The ZoomInOutTool component provides the UI which displays the associated Icon
 * button within a SimpleMap that, when clicked, initiates the corresponding
 * feature.
 *
 * This ZoomInOutTool represents the "Zoom In and Zoom Out Tool" DLS features:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/01_DLS_Components?node-id=1526%3A7422
 *
 * ```typescript
 *
 *   <ZoomInOutTool onZoomOut={function() {
 *     const {zoom} = view;
 *     view.goTo({zoom: zoom - 1});
 *   }} onZoomIn={function() {
 *     const {zoom} = view;
 *     view.goTo({zoom: zoom + 1});
 *   }} />
 *
 * ```
 */
const useStyles = makeStyles((theme: Theme) => ({
  btnGroup : {
    marginLeft : '2px'
  },
  stepperPill : {
    display       : 'flex',
    flexDirection : 'row',
    alignItems    : 'center',
    position      : 'relative',
    width         : '94px',
    height        : '28px',
    background    : '#FFFFFF',
    boxShadow     : '4px 4px 4px rgba(0, 0, 0, 0.25)',
    borderRadius  : '8px'
  },
  stepperButton : {
    flex           : 1,
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    height         : '22px',
    fontFamily     : '-apple-system, system-ui, sans-serif',
    fontWeight     : 400,
    fontSize       : '17px',
    lineHeight     : '22px',
    color          : '#333333',
    cursor         : 'pointer',
    userSelect     : 'none'
  },
  stepperSeparator : {
    width        : '1px',
    alignSelf    : 'stretch',
    margin       : '7px 0',
    background   : 'rgba(60, 60, 67, 0.3)',
    borderRadius : '8px'
  }
}));

export const ZoomInOutTool = ({
  onZoomIn,
  onZoomOut,
  isVertical = true,
  isAdminGeofence = false
}: ZoomInOutProps) => {
  const isPhone      = useIsPhone();
  const classes      = useStyles();
  const {isCurrent}  = useThemeContext();

  return (isAdminGeofence && isCurrent) ? (
    <div className={classes.stepperPill}>
      <Tooltip title={translate('maps:controls.zoomOut')} renderOnlyChildren={isPhone}>
        <div className={classes.stepperButton} onClick={onZoomOut}>
          {'−'}
        </div>
      </Tooltip>
      <div className={classes.stepperSeparator} />
      <Tooltip title={translate('maps:controls.zoomIn')} renderOnlyChildren={isPhone}>
        <div className={classes.stepperButton} onClick={onZoomIn}>
          {'+'}
        </div>
      </Tooltip>
    </div>
  ) : (
    <FloatingIconButtonGroup isVertical={isVertical}>
      <>

        <Tooltip title={translate('maps:controls.zoomIn')} renderOnlyChildren={isPhone}>
          <div>
            <FloatingIconButtonGroup.FloatingIconButton onClick={onZoomIn}>
              <MapZoomInIcon size={IconSize.medium} />
            </FloatingIconButtonGroup.FloatingIconButton>
          </div>
        </Tooltip>

        <Tooltip title={translate('maps:controls.zoomOut')} renderOnlyChildren={isPhone}>
          <div className={!isVertical && classes.btnGroup }>
            <FloatingIconButtonGroup.FloatingIconButton onClick={onZoomOut}>
              <MapZoomOutIcon size={IconSize.medium} />
            </FloatingIconButtonGroup.FloatingIconButton>
          </div>
        </Tooltip>
      </>
    </FloatingIconButtonGroup>
  );
};

export default ZoomInOutTool;
